import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { IOperationResultNoValue, Industry, TemplateStatus } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import { stringify as toQueryString } from 'query-string';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ILocationState } from '../../../../models';
import { IAdminUserSessionComponentProps, ISystemAutomationTemplate } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { ILocalNotification } from '../../../../models/LocalNotifications';
import {
	IndustryAutomationTemplateViewModel,
	IndustryAutomationTemplatesViewModel,
} from '../../../../viewmodels/AdminViewModels';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../../web/components/ConfirmationDialog';
import { LocalNotificationObserver } from '../../../../web/components/LocalNotificationObserver';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../../../../web/components/LocalNotificationObserver/WithNotificationService';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { DuplicateSystemAutomationTemplateModal } from '../../../components/templates/DuplicateSystemAutomationTemplateModal';
import { TemplatesByIndustry } from '../../../components/templates/TemplatesByIndustry';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		RouteComponentProps<any>,
		IErrorMessageComponentProps,
		IAdminUserSessionComponentProps,
		IFullscreenModalComponentProps,
		INotificationServiceComponentProps<ISystemAutomationTemplate> {
	className?: string;
	industry: Industry;
	styles?: StyleDeclarationValue[];
}

interface IState {
	templateToDelete?: IndustryAutomationTemplateViewModel;
	templateToDuplicate?: IndustryAutomationTemplateViewModel;
}

class _AutomationTemplatesByIndustry extends React.Component<IProps, IState> {
	public readonly state: IState = {};
	private mTemplates: IndustryAutomationTemplatesViewModel;

	constructor(props: IProps) {
		super(props);

		this.mTemplates = new IndustryAutomationTemplatesViewModel(props.userSession, props.industry);
	}

	public componentDidMount() {
		const { logApiError, logPageView, industry, errorMessages } = this.props;

		logPageView(`${industry.split(' ').join()}AutomationTemplates`);
		this.mTemplates.load()?.catch((error: IOperationResultNoValue) => {
			logApiError('LoadAutomationTemplates-Error', error);

			errorMessages.pushApiError(error);
		});
	}

	public render() {
		const { className, styles, industry } = this.props;
		const { templateToDelete, templateToDuplicate } = this.state;
		return (
			<div
				className={`${css(styleSheet.container, ...(styles || []))} automation-templates-by-industry ${
					className || ''
				}`}
			>
				<TemplatesByIndustry
					industry={industry}
					name='Automations'
					// @ts-ignore
					onMenuItemClicked={this.onMenuItemClicked}
					// @ts-ignore
					onTemplateClicked={this.onTemplateClicked}
					// @ts-ignore
					templates={this.mTemplates}
				/>
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{
						isOpen: !!templateToDelete,
						onRequestClose: this.onDeleteConfirmationRequestClose,
					}}
					options={DefaultDeleteConfirmationOptions}
					title={
						<span>
							{`Are you sure you want to delete the "${templateToDelete?.automationTemplate?.name || ''}" template?`}
							<br />
							This will delete the template for &nbsp;
							<span className={css(baseStyleSheet.fontBold)}>all industries.</span>
						</span>
					}
				/>
				<DuplicateSystemAutomationTemplateModal
					industryAutomationTemplate={templateToDuplicate}
					isOpen={!!templateToDuplicate}
					onRequestClose={this.onDuplicateTemplateModalRequestClose}
					templates={this.mTemplates}
				/>
				<LocalNotificationObserver
					topic={Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE}
					onNotificationReceived={this.onEditSystemTemplateNotification}
				/>
			</div>
		);
	}

	private onDuplicateTemplateModalRequestClose = () => {
		this.setState({
			templateToDuplicate: null,
		});
	};

	private onDeleteConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		const { templateToDelete } = this.state;
		if (!canceled && !!result?.representedObject) {
			const { logEvent, errorMessages, logApiError } = this.props;

			const promise = templateToDelete.automationTemplate.delete();
			if (promise) {
				logEvent('DeleteTemplate', {
					id: templateToDelete.automationTemplate.id,
				});
				promise
					.then(() => {
						const itemsToRemove = [templateToDelete];

						this.mTemplates.drafts.removeItems(itemsToRemove);

						this.mTemplates.archived.removeItems(itemsToRemove);

						this.mTemplates.published.removeItems(itemsToRemove);
					})
					.catch((error: IOperationResultNoValue) => {
						logApiError('DeleteAction-Error', error);

						errorMessages.pushApiError(error);
					});
			}
		}
		this.setState({
			templateToDelete: null,
		});
	};

	private onTemplateClicked = (template: IndustryAutomationTemplateViewModel) => {
		const { fullscreenModal, logInput, industry } = this.props;

		logInput('Edit', 'Click', { id: template.id, industry });
		const locationState: ILocationState<IndustryAutomationTemplateViewModel, any> = { viewModel: template };
		fullscreenModal?.history?.push({
			pathname: `/automations/${template.id}`,
			search: toQueryString({ industry }),
			state: locationState,
		});
	};

	private onMenuItemClicked = (
		template: IndustryAutomationTemplateViewModel,
		menuItem: 'edit' | 'archive' | 'delete' | 'publish' | 'copy'
	) => {
		const { fullscreenModal, logInput, industry, logApiError, errorMessages, postNotification } = this.props;
		switch (menuItem) {
			case 'copy': {
				logInput('CopyAction', 'Click', { id: template.id, industry });
				this.setState({
					templateToDuplicate: template,
				});
				break;
			}
			case 'edit': {
				logInput('EditAction', 'Click', { id: template.id, industry });
				const locationState: ILocationState<IndustryAutomationTemplateViewModel, any> = { viewModel: template };
				fullscreenModal?.history?.push({
					pathname: `/automations/${template.id}`,
					search: toQueryString({ industry }),
					state: locationState,
				});
				break;
			}
			case 'publish': {
				const promise = this.mTemplates.publishTemplate(template, industry);
				if (promise) {
					logInput('PublishAction', 'Click', { id: template.id, industry });
					promise
						.then(publishedTemplateModel => {
							postNotification({
								info: publishedTemplateModel,
								topic: Topics.EDIT_SYSTEM_AUTOMATION_TEMPLATE,
							});
						})
						.catch((error: IOperationResultNoValue) => {
							logApiError('PublishAction-Error', error);

							errorMessages.pushApiError(error);
						});
				}
				break;
			}
			case 'delete': {
				logInput('DeleteAction', 'Click', { id: template.id, industry });
				this.setState({
					templateToDelete: template,
				});
				break;
			}
			case 'archive': {
				const promise = this.mTemplates.archiveTemplate(template, industry);
				if (promise) {
					logInput('ArchiveAction', 'Click', { id: template.id, industry });
					promise.catch((error: IOperationResultNoValue) => {
						logApiError('ArchiveAction-Error', error);

						errorMessages.pushApiError(error);
					});
				}
				break;
			}
			default: {
				break;
			}
		}
	};

	private onEditSystemTemplateNotification = (notification?: ILocalNotification<ISystemAutomationTemplate>) => {
		const { industry } = this.props;
		const template =
			this.mTemplates.drafts.getById(notification.info.template.id) ||
			this.mTemplates.published.getById(notification.info.template.id) ||
			this.mTemplates.archived.getById(notification.info.template.id);
		if (template) {
			template.setSystemTemplate(notification.info);
			const status = template.getStatusForIndustry(industry);
			if (status === TemplateStatus.Published && !this.mTemplates.published.has(template)) {
				// template published... move it
				const toRemove = [template];
				this.mTemplates.drafts.removeItems(toRemove);
				this.mTemplates.archived.removeItems(toRemove);
				this.mTemplates.published.add(template);
			} else if (status === TemplateStatus.Archived && !this.mTemplates.archived.has(template)) {
				// template archived... move it
				const toRemove = [template];
				this.mTemplates.drafts.removeItems(toRemove);
				this.mTemplates.published.removeItems(toRemove);
				this.mTemplates.archived.add(template);
			}
		}
	};
}

const AutomationTemplatesByIndustryAsObserver = observer(_AutomationTemplatesByIndustry);
const AutomationTemplatesByIndustryWithContext = inject(
	UserSessionViewModelKey,
	FullScreenModalViewModelKey,
	ErrorMessagesViewModelKey
)(AutomationTemplatesByIndustryAsObserver);
const AutomationTemplatesByIndustryWithNotificationService = withNotificationService(
	AutomationTemplatesByIndustryWithContext
);
export const AutomationTemplatesByIndustry = withEventLogging(
	AutomationTemplatesByIndustryWithNotificationService,
	'AutomationTemplatesByIndustry'
);
